import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, ConfigProvider, Segmented, Space } from 'antd';
import { FctTarjeta } from '../fct/react';
import { fct } from '../fct/tokens/tokens';
import { getBandeja } from '../api/cxPanelApi';
import BandejaTab from '../components/EncuestasCx/BandejaTab';
import CampanaSemanaTab from '../components/EncuestasCx/CampanaSemanaTab';
import MysteryF4Tab from '../components/EncuestasCx/MysteryF4Tab';
import RegistradasTab from '../components/EncuestasCx/RegistradasTab';
import { contarAbiertas, plural } from '../components/EncuestasCx/panelCx';

type Vista = 'semana' | 'registradas' | 'f4' | 'pendientes';

/**
 * Selectores (vista, Por enviar / Enviados, Todas / Respondidas…) sobre la tarjeta blanca: el estilo del manual
 * (pista casi blanca y seleccionado blanco) está pensado para el lienzo verdoso y aquí no se distinguía. Pista en el
 * tono del lienzo y seleccionado en blanco con texto verde oscuro.
 */
const TEMA_PANEL = {
  components: {
    Segmented: {
      trackBg: fct.superficie.lienzo,
      itemSelectedBg: fct.superficie.tarjeta,
      itemSelectedColor: fct.marca['verde-oscuro'],
      itemColor: fct.tinta['700'],
      itemHoverColor: fct.marca['verde-oscuro'],
      itemHoverBg: 'rgba(255,255,255,.55)',
    },
  },
};

/**
 * Contenedor de una vista. Sus desplegables, tooltips, calendarios y fichas se pintan DENTRO de él (y no
 * en el body), así que se ocultan con la vista al cambiar: antes se quedaban flotando encima de la otra.
 */
const ContenedorVista: React.FC<{ visible: boolean; children: React.ReactNode }> = ({ visible, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Modales y paneles laterales (Ant Design los pide con document.body) siguen en el body: dentro de la vista
  // no se cerraban. Solo desplegables, tooltips y calendarios van dentro de la vista.
  const contenedor = useCallback(
    // Lo que se abre DESDE un modal o un panel lateral (p. ej. el calendario de la ficha F4) va al body: si fuera
    // a la vista, quedaría detrás del panel y no se vería.
    (disparador?: HTMLElement) => (disparador && ref.current?.contains(disparador) ? ref.current : document.body),
    [],
  );
  return (
    <div ref={ref} style={{ display: visible ? undefined : 'none', position: 'relative' }}>
      <ConfigProvider getPopupContainer={contenedor} theme={TEMA_PANEL}>{children}</ConfigProvider>
    </div>
  );
};

/**
 * Panel interno «Encuestas CX» (módulo CXCP_ENCUESTAS). Lo ven los grupos de Entra CxCp y
 * Dpto. Sistemas a través del recurso ACL `Cx.Panel.Read` (ruta y menú).
 *
 * Una sola barra: a la izquierda el título, la vista y el filtro de tipos con contadores; a la
 * derecha los controles de cada vista (semana o rango, buscador, actualizar). La cabecera con
 * título y vista la monta la página y cada vista la coloca al principio de su barra.
 * - «Campaña»: lo que el flujo del jueves mandaría (ROC/OC/SI de la semana anterior).
 * - «Registradas»: consulta de todas las encuestas registradas (cualquier tipo), su estado y sus
 *   respuestas; para las F4, también la visibilidad para Mystery. Vista pasiva: las respuestas
 *   llegan solas del formulario, aquí no se da de alta nada.
 * - «Mystery (F4)»: todas las encuestas F4 y su visibilidad para Mystery (quitar / poner).
 * - «Pendientes»: la bandeja de envíos por evento (V2, F2, F4 online) que CxCp aprueba o
 *   descarta. Su contador son los envíos ABIERTOS (pendientes, bloqueados y aprobados sin enviar: lo
 *   mismo que el backend devuelve con `estado=abiertas`); se pide una vez al entrar en la página y la
 *   vista lo refresca cada vez que recarga o actúa.
 *
 * Las vistas quedan montadas (solo se ocultan) para no perder semana, rango y filtros al cambiar;
 * todas menos «Campaña» se montan la primera vez que se abren. El texto del buscador es común:
 * buscar un cliente responde a la vez «¿entra esta semana?», «¿qué encuestas tiene?» y «¿tiene
 * algún correo por aprobar?».
 */
const EncuestasCxPage: React.FC = () => {
  const [vista, setVistaEstado] = useState<Vista>('semana');
  const [vistasAbiertas, setVistasAbiertas] = useState<Set<Vista>>(() => new Set<Vista>(['semana']));
  const setVista = (v: Vista) => {
    // Cierra lo que hubiera abierto en la vista que se deja (un desplegable se cierra al perder el foco).
    (document.activeElement as HTMLElement | null)?.blur?.();
    setVistaEstado(v);
    setVistasAbiertas((s) => (s.has(v) ? s : new Set(s).add(v)));
  };
  const [busqueda, setBusqueda] = useState('');

  /** Envíos abiertos de la bandeja (null = aún no se sabe o no se pudo saber: sin contador). */
  const [abiertas, setAbiertas] = useState<number | null>(null);
  /** Si la vista ya ha informado, su dato manda sobre el de la consulta inicial (más antigua). */
  const informadoPorVista = useRef(false);
  const informarAbiertas = useCallback((n: number) => {
    informadoPorVista.current = true;
    setAbiertas(n);
  }, []);

  useEffect(() => {
    let vigente = true;
    getBandeja({ estado: 'abiertas' })
      .then((r) => { if (vigente && !informadoPorVista.current) setAbiertas(contarAbiertas(r.items)); })
      // Sin contador si falla: la vista dirá qué pasa al abrirla.
      .catch(() => undefined);
    return () => { vigente = false; };
  }, []);

  // Ayuda como título nativo, no como Tooltip: la cabecera se repite en cada vista y un Tooltip se quedaba
  // abierto al ocultarse la vista desde la que se pulsó.
  const ayudaPendientes = abiertas == null
    ? 'Correos que prepara el sistema y esperan aprobación'
    : `${plural(abiertas, 'envío abierto', 'envíos abiertos')}: pendientes de aprobar, bloqueados y aprobados que aún no han salido`;
  const etiquetaPendientes = (
    <Space size={6} align="center">
      <span>Pendientes</span>
      {abiertas != null && (
        <Badge
          count={abiertas}
          showZero
          overflowCount={999}
          style={{ backgroundColor: abiertas > 0 ? fct.marca['verde-oscuro'] : fct.tinta['500'] }}
        />
      )}
    </Space>
  );

  const cabecera = (
    <Space size={12} align="center">
      <span style={{
        paddingLeft: 10, borderLeft: `3px solid ${fct.marca['verde-base']}`,
        color: fct.tinta['900'], fontWeight: 500, fontSize: 16, whiteSpace: 'nowrap',
      }}>
        Encuestas CX
      </span>
      <Segmented<Vista>
        value={vista}
        onChange={setVista}
        options={[
          { label: 'Campaña', value: 'semana', title: 'Campaña de la semana' },
          { label: 'Registradas', value: 'registradas' },
          { label: 'Mystery (F4)', value: 'f4' },
          { label: etiquetaPendientes, value: 'pendientes', title: ayudaPendientes },
        ]}
      />
    </Space>
  );

  return (
    <FctTarjeta>
      <ContenedorVista visible={vista === 'semana'}>
        <CampanaSemanaTab cabecera={cabecera} busqueda={busqueda} onBusqueda={setBusqueda} />
      </ContenedorVista>
      {/* El resto se monta la primera vez que se abre (no consulta antes de hacer falta). */}
      {(vista === 'registradas' || vistasAbiertas.has('registradas')) && (
        <ContenedorVista visible={vista === 'registradas'}>
          <RegistradasTab cabecera={cabecera} busqueda={busqueda} onBusqueda={setBusqueda} />
        </ContenedorVista>
      )}
      {(vista === 'f4' || vistasAbiertas.has('f4')) && (
        <ContenedorVista visible={vista === 'f4'}>
          <MysteryF4Tab cabecera={cabecera} busqueda={busqueda} onBusqueda={setBusqueda} />
        </ContenedorVista>
      )}
      {(vista === 'pendientes' || vistasAbiertas.has('pendientes')) && (
        <ContenedorVista visible={vista === 'pendientes'}>
          <BandejaTab cabecera={cabecera} busqueda={busqueda} onBusqueda={setBusqueda} onAbiertas={informarAbiertas} />
        </ContenedorVista>
      )}
    </FctTarjeta>
  );
};

export default EncuestasCxPage;
